import {
  toFiniteNumber,
  toRowLocation,
  toText,
  type LayerAnalyticsCard,
  type LayerAnalyticsChart,
  type LayerAnalyticsFeature,
  type LayerAnalyticsFigure,
  type LayerAnalyticsResult,
  type LayerAnalyticsTable,
} from './layerAnalytics.utils';

/*
 * Wildfire template (one card):
 *   figures  Incidents · Daily Acres (ac)
 *   charts   Cause (Natural / Human / Undetermined, plus Unknown when present)
 *   table    Largest Incidents: Name · U.S. State · Acres · Contained (%)
 */

/** Wildfire incident points carry these attributes (e.g. USA Current Wildfires). */
export const isWildfireIncidentFeature = ({
  attributes,
}: LayerAnalyticsFeature) =>
  'IncidentName' in attributes && 'DailyAcres' in attributes;

export type WildfireIncident = {
  name: string;
  state: string;
  acres: number | null;
  percentContained: number | null;
  cause: string;
  longitude: number | null;
  latitude: number | null;
};

export const toWildfireIncident = ({
  attributes,
  longitude,
  latitude,
}: LayerAnalyticsFeature): WildfireIncident => ({
  name: toText(attributes.IncidentName),
  state: toText(attributes.POOState),
  acres: toFiniteNumber(attributes.DailyAcres),
  percentContained: toFiniteNumber(attributes.PercentContained),
  cause: toText(attributes.FireCause),
  longitude: longitude ?? null,
  latitude: latitude ?? null,
});

export const formatWildfireAcres = (acres: number | null): string =>
  acres === null ? '' : Math.round(acres).toLocaleString('en-US');

export const formatWildfirePercentContained = (value: number | null): string =>
  value === null ? '' : String(Math.round(value));

export const WILDFIRE_TABLE_TITLE = 'Largest Incidents';
export const WILDFIRE_TABLE_LIMIT = 10;

/** The biggest incidents by acres, largest first; unknown acres sort last. */
export const getLargestWildfireIncidents = (
  incidents: WildfireIncident[],
  limit: number = WILDFIRE_TABLE_LIMIT
): WildfireIncident[] =>
  [...incidents]
    .sort(
      (a, b) =>
        (b.acres ?? Number.NEGATIVE_INFINITY) -
          (a.acres ?? Number.NEGATIVE_INFINITY) || 0
    )
    .slice(0, limit);

export const WILDFIRE_COLUMNS = [
  { key: 'state', label: 'U.S. State' },
  { key: 'acres', label: 'Acres' },
  { key: 'contained', label: 'Contained (%)' },
] as const;

/** Fire causes (`FireCause`) with their own chart slice, in display order. */
export const WILDFIRE_CAUSES = ['Natural', 'Human', 'Undetermined'] as const;
const UNKNOWN_CAUSE = 'Unknown';

/** Display label for a `FireCause`; blank or unrecognised is "Unknown". */
const toCauseLabel = (cause: string): string =>
  WILDFIRE_CAUSES.find(
    (candidate) => candidate.toLowerCase() === cause.trim().toLowerCase()
  ) ?? UNKNOWN_CAUSE;

/** Incident count per cause; "Unknown" is only listed when it occurs. */
export const buildWildfireCauseChart = (
  incidents: WildfireIncident[]
): LayerAnalyticsChart => {
  const counts: Record<string, number> = Object.fromEntries(
    WILDFIRE_CAUSES.map((cause) => [cause, 0])
  );
  incidents.forEach(({ cause }) => {
    const label = toCauseLabel(cause);
    counts[label] = (counts[label] ?? 0) + 1;
  });
  return {
    title: 'Cause',
    slices: [...WILDFIRE_CAUSES, UNKNOWN_CAUSE]
      .filter((label) => label !== UNKNOWN_CAUSE || counts[label])
      .map((label) => ({ label, count: counts[label] ?? 0 })),
  };
};

/** Totals shown above the wildfire table, over all incidents. */
export const buildWildfireFigures = (
  incidents: WildfireIncident[]
): LayerAnalyticsFigure[] => [
  { label: 'Incidents', value: incidents.length },
  {
    label: 'Daily Acres',
    value: incidents.reduce(
      (total, incident) => total + (incident.acres ?? 0),
      0
    ),
    unit: 'ac',
  },
];

/** The largest incidents only; the figures and chart cover all of them. */
export const buildWildfireTable = (
  incidents: WildfireIncident[]
): LayerAnalyticsTable => ({
  title: WILDFIRE_TABLE_TITLE,
  nameLabel: 'Name',
  columns: WILDFIRE_COLUMNS.map(({ key, label }) => ({ key, label })),
  rows: getLargestWildfireIncidents(incidents).map((incident) => ({
    name: incident.name,
    cells: {
      state: incident.state,
      acres: formatWildfireAcres(incident.acres),
      contained: formatWildfirePercentContained(incident.percentContained),
    },
    ...toRowLocation(incident),
  })),
});

export const buildWildfireCard = (
  incidents: WildfireIncident[]
): LayerAnalyticsCard => ({
  key: 'wildfire',
  title: null,
  figures: buildWildfireFigures(incidents),
  charts: [buildWildfireCauseChart(incidents)],
  table: buildWildfireTable(incidents),
});

export const buildWildfireResult = ({
  layerValue,
  label,
  features,
}: {
  layerValue: string;
  label: string;
  features: LayerAnalyticsFeature[];
}): LayerAnalyticsResult => {
  const incidents = features
    .filter(isWildfireIncidentFeature)
    .map(toWildfireIncident);
  return {
    layerValue,
    label,
    hazard: 'wildfire',
    featureCount: incidents.length,
    cards: incidents.length === 0 ? [] : [buildWildfireCard(incidents)],
  };
};
